import { Injectable, inject, signal } from '@angular/core';
import { LookupResponse } from '@core/models/common.model';
import { ToastService } from '@core/services/toast.service';
import { LookupApiService } from '@core/lookup/lookup-api.service';
import { LOOKUP_CODES } from '@core/lookup/lookup-codes';

// Which lookup list each key of the assessment form's lookups comes from.
export const CIA_LOOKUP_CODES = {
  durationScales: LOOKUP_CODES.durationScale,
  severityScales: LOOKUP_CODES.severityScale,
  problemClusters: LOOKUP_CODES.problemCluster,
  problems: LOOKUP_CODES.problem,
  riskRatings: LOOKUP_CODES.riskRating,
  developmentStages: LOOKUP_CODES.developmentStage,
  functionalImpactSeverities: LOOKUP_CODES.functionalImpactSeverity,
  clientLimitationTypes: LOOKUP_CODES.clientLimitationType,
  riskScreeningTypes: LOOKUP_CODES.riskScreeningType,
  symptomClusterComplexityTypes: LOOKUP_CODES.symptomClusterComplexityType,
  patternTypes: LOOKUP_CODES.patternType,
  workPerformanceImpactTypes: LOOKUP_CODES.workPerformanceImpactType,
  fourPFactorTypes: LOOKUP_CODES.fourPFactorType,
  fourPDimensionTypes: LOOKUP_CODES.fourPDimensionType,
} as const;

// The reference lists of the assessment form. They are loaded with their inactive items, so an assessment saved with an item that has since
// been retired still shows its name; that is why they are not kept in the shared lookup store. The assessment itself is part of the case and
// lives in the InterventionStore, like the other parts of the case.
@Injectable({ providedIn: 'root' })
export class CiaLookupStore {
  private readonly lookupApi = inject(LookupApiService);
  private readonly toast = inject(ToastService);
  readonly lookups = signal<Record<string, LookupResponse[]>>({});
  readonly lookupsLoading = signal(false);

  loadLookups(): void {
    this.lookupsLoading.set(true);
    const keyedCodes = Object.entries(CIA_LOOKUP_CODES);

    this.lookupApi.getLookups([...new Set(keyedCodes.map(([, code]) => code))], true).subscribe({
      next: (loaded) => {
        this.lookups.set(Object.fromEntries(keyedCodes.map(([key, code]) => [key, loaded[code] ?? []])));
        this.lookupsLoading.set(false);
      },
      error: () => {
        this.toast.error('Failed to load assessment lookup data');
        this.lookupsLoading.set(false);
      },
    });
  }
}
